// src/encounter/lib/to-patient-contacts.ts
// Map the patient's emergency/relative contacts (Flutter `emergencyContactResponse` → ContactWidget)
// into a small view model for the card's contacts block. PROD-1329 ("relative contacts").
import type { PatientContactRelation, PatientEmergencyContact } from '@/types/patient-detail-api';

export interface ContactVM {
  id: string;
  /** Full name (first/middle/last joined). */
  name: string;
  /** Relationship label ("Sister", "Father") — the coded `relation.name` only (the raw
   *  `relationship` field is a GUID id, not a label, so it is never used). */
  relation?: string;
  phone?: string;
  /** Proxy / guardian flag → renders a "Proxy" chip (Flutter's green Proxy badge). */
  isProxy?: boolean;
}

function fullName(c: PatientEmergencyContact): string {
  return [c.firstName, c.middleName, c.lastName].filter((p) => p && String(p).trim()).join(' ').trim();
}

/** The relationship display ("Sister", "Self").
 *
 *  Reads BOTH spellings because the service PascalCases this one nested object while every sibling
 *  field on the contact is camelCase — `"relation":{"Id":"…","Name":"Sister"}` measured on
 *  `GET /api/Patients/{id}` (dev, 2026-09-08). Reading `name` alone resolved `undefined` for every
 *  contact on every patient, so the card silently dropped the line Flutter draws
 *  (`patient_info_widget.dart:346`). Do not collapse this back to a single key.
 *
 *  Still NEVER falls back to the sibling `relationship` field: that is a relationship-type GUID,
 *  not a label, and falling back to it once surfaced a GUID on the card. */
function relationName(relation: PatientContactRelation | null | undefined): string | undefined {
  return relation?.name?.trim() || relation?.Name?.trim() || undefined;
}

/** Build the contacts view model — drops deleted + nameless rows (a contact with no name and no
 *  phone is not renderable). Order is preserved (wire order = the patient's entry order). */
export function toContacts(
  list: (PatientEmergencyContact | null)[] | null | undefined,
): ContactVM[] {
  const out: ContactVM[] = [];
  (list ?? []).forEach((c, i) => {
    if (!c || c.isDeleted) return;
    const name = fullName(c);
    const phone = c.contactNumber?.trim() || undefined;
    if (!name && !phone) return; // nothing to show
    out.push({
      id: c.emergencyContactId || `contact-${i}`,
      name: name || 'Contact',
      // Use ONLY the coded relation's display name (Flutter `data.relation.name`), in either
      // casing the service emits — see `relationName`.
      relation: relationName(c.relation),
      ...(phone ? { phone } : {}),
      ...(c.isProxy ? { isProxy: true } : {}),
    });
  });
  return out;
}
